import React, { useEffect, useState, useMemo } from 'react'; // Added useMemo
import { QRCodeSVG } from 'qrcode.react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import './ManajemenAlatGelas.css';

const initialForm = { nama: '', kode: '', spesifikasi: '', lokasi: '', stok: '', gambarUrl: '', sopUrl: '' }; // Added stok

const ManajemenAlatGelas = () => {
  const [alatGelas, setAlatGelas] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [gambarFile, setGambarFile] = useState(null);
  const [sopFile, setSopFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState(''); // State for search term
  const [selectedImage, setSelectedImage] = useState(null); // State for image popup
  const [sortColumn, setSortColumn] = useState(null); // State for sort column
  const [sortDirection, setSortDirection] = useState('asc'); // State for sort direction (asc/desc)
  const navigate = useNavigate(); // Hook for navigation

  // Fetch data
  const fetchAlatGelas = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'alat_gelas'));
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAlatGelas(data);
    } catch (err) {
      setError('Gagal mengambil data alat gelas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlatGelas(); }, []);

  // Filtered and sorted list based on search term and sort settings
  const filteredAlatGelas = useMemo(() => {
    let filtered = alatGelas;
    
    // Apply search filter
    if (searchTerm) {
      filtered = alatGelas.filter(item =>
        item.nama.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.kode && item.kode.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];
        
        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';
        
        // Convert to string for comparison if not numbers
        if (sortColumn === 'stok') {
          // For numeric columns, convert to number
          aValue = Number(aValue) || 0;
          bValue = Number(bValue) || 0;
        } else {
          // For text columns, convert to lowercase string
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        }
        
        // Compare values
        if (aValue < bValue) {
          return sortDirection === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filtered;
  }, [alatGelas, searchTerm, sortColumn, sortDirection]);

  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };


  // Handle form input
  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Handle file input
  const handleGambarChange = e => {
    setGambarFile(e.target.files[0]);
  };
  const handleSOPChange = e => {
    setSopFile(e.target.files[0]);
  };

  // Upload file helper
  const uploadFile = async (file, folder) => {
    if (!file) return '';
    const fileRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };
  // Delete file helper
  const deleteFileByUrl = async url => {
    if (!url) return;
    try {
      // Attempt to create a reference from the download URL
      const fileRef = ref(storage, url);
      await deleteObject(fileRef);
      console.log("Successfully deleted file:", url);
    } catch (error) {
      // Log error if deletion fails
      console.error("Error deleting file from URL:", url, error);
      // Optionally inform the user or handle differently
    }
  };

  // Add or update
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    let success = false; // Flag to track success

    try {
      let gambarUrl = form.gambarUrl;
      let sopUrl = form.sopUrl;

      // Handle upload gambar
      if (gambarFile) {
        if (gambarUrl) await deleteFileByUrl(gambarUrl); // Delete old one if exists and uploading new
        gambarUrl = await uploadFile(gambarFile, 'alatgelas_gambar');
      }
      // Handle upload SOP
      if (sopFile) {
        if (sopUrl) await deleteFileByUrl(sopUrl); // Delete old one if exists and uploading new
        sopUrl = await uploadFile(sopFile, 'alatgelas_sop');
      }

      // Ensure stok is saved as a number
      const dataToSave = {
        ...form,
        stok: Number(form.stok) || 0, // Convert stok to number, default to 0 if invalid
        gambarUrl,
        sopUrl
      };

      if (editId) {
        await updateDoc(doc(db, 'alat_gelas', editId), dataToSave);
        console.log("Glassware updated successfully:", editId);
      } else {
        const docRef = await addDoc(collection(db, 'alat_gelas'), dataToSave);
        console.log("Glassware added successfully:", docRef.id);
      }

      success = true; // Mark as successful

    } catch (err) {
      console.error("Error saving glassware:", err);
      setError(`Gagal menyimpan data: ${err.message || 'Unknown error'}`);
      // Keep form data for correction, don't reset on error
    } finally {
      setLoading(false); // Always stop loading indicator
      if (success) {
        // Reset form only on successful save
        setForm(initialForm);
        setEditId(null);
        setGambarFile(null);
        setSopFile(null);
        fetchAlatGelas(); // Refresh the list
      }
    }
  };

  // Edit
  const handleEdit = item => {
    setForm({
      nama: item.nama || '',
      kode: item.kode || '',
      spesifikasi: item.spesifikasi || '',
      lokasi: item.lokasi || '',
      stok: item.stok || '', // Added stok
      gambarUrl: item.gambarUrl || '',
      sopUrl: item.sopUrl || '',
    });
    setEditId(item.id);
    setGambarFile(null);
    setSopFile(null);
  };

  // Delete
  const handleDelete = async id => {
    if (!window.confirm('Hapus alat gelas ini?')) return;
    setLoading(true);
    setError(''); // Clear previous errors
    try {
      const item = alatGelas.find(x => x.id === id);
      if (item?.gambarUrl) await deleteFileByUrl(item.gambarUrl);
      if (item?.sopUrl) await deleteFileByUrl(item.sopUrl);
      await deleteDoc(doc(db, 'alat_gelas', id));
      fetchAlatGelas(); // Refresh list
    } catch (err) {
      console.error("Error deleting glassware:", err);
      setError(`Gagal menghapus data: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false); // Ensure loading is stopped
    }
  };

  // Handler for "Lihat Jadwal" button
  const handleLihatJadwal = (item) => {
    // Navigate to the Admin's report/schedule page
    navigate('/admin/laporan-penggunaan-alat', {
       state: { alatId: item.id, namaAlat: item.nama, type: 'alat_gelas' } // Pass necessary info
    });
  };

  // Handler for image click
  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  // Handler to close image popup
  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  // Add states for popup management
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);

  // Modified handleEdit to show popup
  const handleEditClick = item => {
    handleEdit(item);
    setShowEditPopup(true);
  };

  // Handler for "Tambah Item" button
  const handleAddClick = () => {
    setForm(initialForm);
    setEditId(null);
    setError('');
    setGambarFile(null);
    setSopFile(null);
    setShowAddPopup(true);
  };

  // Close popup handler
  const handleClosePopup = () => {
    if (editId) {
      setForm(initialForm);
      setEditId(null);
      setError('');
      setGambarFile(null);
      setSopFile(null);
    }
    setShowAddPopup(false);
    setShowEditPopup(false);
  };

  return (
    <div className="manajemen-alatgelas-container">
      <div className="header-section">
        <h2>Manajemen Alat Gelas</h2>
        <button 
          onClick={handleAddClick} 
          className="btn btn-primary btn-add-alatgelas"
        >
          Tambah Alat Gelas
        </button>
      </div>

      {/* Display Error and Loading */}
      {error && <div className="error-message">{error}</div>}
      {loading && alatGelas.length === 0 && <div className="loading-message">Memuat data...</div>}

      {/* Simple Search Input */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Cari alat gelas berdasarkan nama atau kode..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>


      {/* Add/Edit Popup */}
      {(showAddPopup || showEditPopup) && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="popup-content"
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '5px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3>{editId ? 'Edit Alat Gelas' : 'Tambah Alat Gelas Baru'}</h3>
              <button 
                onClick={handleClosePopup}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <input name="nama" placeholder="Nama Alat Gelas" value={form.nama} onChange={handleChange} required className="form-input" />
                <input name="kode" placeholder="Kode Unik" value={form.kode} onChange={handleChange} required className="form-input" />
              </div>
              <div className="form-row">
                <input name="spesifikasi" placeholder="Spesifikasi" value={form.spesifikasi} onChange={handleChange} className="form-input" />
                <input name="lokasi" placeholder="Lokasi Penyimpanan" value={form.lokasi} onChange={handleChange} className="form-input" />
                <input name="stok" placeholder="Stok" type="number" value={form.stok} onChange={handleChange} required className="form-input" /> {/* Added Stok Input */}
              </div>
              <div className="form-row file-inputs">
                <label className="file-label">
                  Gambar:
                  <input type="file" accept="image/*" onChange={handleGambarChange} className="file-input" />
                  {gambarFile && <span className="file-name">{gambarFile.name}</span>}
                </label>
                <label className="file-label">
                  SOP (PDF/DOC):
                  <input type="file" accept="application/pdf,application/msword,.doc,.docx" onChange={handleSOPChange} className="file-input" />
                  {sopFile && <span className="file-name">{sopFile.name}</span>}
                </label>
              </div>
              {editId && (
                <div className="current-files">
                  {form.gambarUrl && (
                    <div className="current-file-item">
                      <span>Gambar Saat Ini: </span>
                      <img src={form.gambarUrl} alt="Current Alat Gelas" style={{ maxWidth: '100px', maxHeight: '60px', verticalAlign: 'middle' }} />
                    </div>
                  )}
                  {form.sopUrl && (
                    <div className="current-file-item">
                      <span>SOP Saat Ini: </span>
                      <a href={form.sopUrl} target="_blank" rel="noopener noreferrer">Lihat SOP</a>
                    </div>
                  )}
                </div>
              )}
              <div className="form-actions" style={{ marginTop: '15px' }}>
                <button type="submit" disabled={loading} className="btn btn-primary">
                  {loading ? 'Menyimpan...' : (editId ? 'Update Alat Gelas' : 'Tambah Alat Gelas')}
                </button>
                <button
                  type="button"
                  onClick={handleClosePopup}
                  disabled={loading}
                  className="btn btn-secondary"
                  style={{ marginLeft: '10px' }}
                >
                  Batal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="alatgelas-table-container">
        <table className="alatgelas-table">
          <thead>
            <tr>
              <th 
                className="sortable-header" 
                onClick={() => handleSort('nama')}
                style={{ cursor: 'pointer' }}
              >
                Nama
                {sortColumn === 'nama' && (
                  <span className="sort-indicator">
                    {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                  </span>
                )}
              </th>
              <th 
                className="sortable-header" 
                onClick={() => handleSort('kode')}
                style={{ cursor: 'pointer' }}
              >
                Kode
                {sortColumn === 'kode' && (
                  <span className="sort-indicator">
                    {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                  </span>
                )}
              </th>
              <th 
                className="sortable-header" 
                onClick={() => handleSort('spesifikasi')}
                style={{ cursor: 'pointer' }}
              >
                Spesifikasi
                {sortColumn === 'spesifikasi' && (
                  <span className="sort-indicator">
                    {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                  </span>
                )}
              </th>
              <th 
                className="sortable-header" 
                onClick={() => handleSort('lokasi')}
                style={{ cursor: 'pointer' }}
              >
                Lokasi
                {sortColumn === 'lokasi' && (
                  <span className="sort-indicator">
                    {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                  </span>
                )}
              </th>
              <th 
                className="sortable-header" 
                onClick={() => handleSort('stok')}
                style={{ cursor: 'pointer' }}
              >
                Stok
                {sortColumn === 'stok' && (
                  <span className="sort-indicator">
                    {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                  </span>
                )}
              </th>
              <th>Gambar</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {/* Use filtered list */}
            {filteredAlatGelas.map(item => (<tr key={item.id}>
                <td>{item.nama}</td>
                <td>{item.kode}</td>
                <td>{item.spesifikasi}</td>
                <td>{item.lokasi}</td>
                <td>{item.stok}</td> {/* Added Stok Data */}
                <td>
                  {item.gambarUrl ? (
                    <img 
                      src={item.gambarUrl} 
                      alt={item.nama} 
                      className="table-image" 
                      onClick={() => handleImageClick(item.gambarUrl)}
                      style={{ cursor: 'pointer' }}
                    />
                  ) : 'N/A'}
                </td>
                 {/* Updated Action Buttons */}
                <td className="action-buttons">
                  <button onClick={() => handleEditClick(item)} className="btn btn-edit" disabled={loading}>Edit</button>
                  <button onClick={() => handleDelete(item.id)} className="btn btn-delete" disabled={loading}>Hapus</button>
                </td>
              </tr>))}
            {/* Update empty state message */}
            {filteredAlatGelas.length === 0 && !loading && (<tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>
                   {searchTerm ? 'Tidak ada alat gelas yang cocok dengan pencarian.' : 'Belum ada data alat gelas.'}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Image Popup Modal */}
      {selectedImage && (
        <div 
          className="image-popup-overlay" 
          onClick={handleCloseImage}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="image-popup-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              maxWidth: '90%',
              maxHeight: '90%',
              backgroundColor: 'white',
              padding: '10px',
              borderRadius: '5px'
            }}
          >
            <button 
              onClick={handleCloseImage}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              &times;
            </button>
            <img 
              src={selectedImage} 
              alt="Enlarged view" 
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                display: 'block'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ManajemenAlatGelas;
